import { useEffect, useState } from "react";
import axios from "axios";
import RecordPaymentModal from "./RecordPaymentModal";
import { useAccountant } from "@/contexts/AccountantContext";
import { openAuthenticatedPdf } from "@/lib/downloadExport";
import { formatAppDate } from "@/lib/formatDate";
import KipaIcon from "@/components/kipa/KipaIcon";
import {
  CheckCircle2,
  CircleDollarSign,
  Clock,
  Download,
  ScrollText,
  Share2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomersScreen({ reloadKey }) {
  const { fmt } = useAccountant();
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/customers`).then((r) => setCustomers(r.data)).catch(() => {});
  }, [reloadKey]);

  const openCustomer = async (name) => {
    try {
      const r = await axios.get(`${API}/customers/${encodeURIComponent(name)}`);
      setSelected(r.data);
    } catch (e) {
      console.error(e);
    }
  };

  if (selected) {
    return <CustomerDetail customer={selected} onBack={() => setSelected(null)} />;
  }

  const totalRevenue = customers.reduce((s, c) => s + c.total_spent, 0);
  const totalOutstanding = customers.reduce((s, c) => s + c.outstanding, 0);

  return (
    <div className="flex flex-col h-full bg-[var(--bg2)]" data-testid="customers-screen">
      <div className="bg-white px-4 py-3 border-b border-[var(--br)] flex gap-2">
        <div className="flex-1 bg-[var(--sf2)] rounded-xl p-2 text-center">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)]">Customers</div>
          <div className="font-extrabold text-[14px] text-[var(--tx)]">{customers.length}</div>
        </div>
        <div className="flex-1 bg-[var(--sf2)] rounded-xl p-2 text-center">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)]">Revenue</div>
          <div className="font-extrabold text-[14px] text-[var(--gn)]">{fmt(totalRevenue)}</div>
        </div>
        <div className="flex-1 bg-[var(--sf2)] rounded-xl p-2 text-center">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)]">Owed</div>
          <div className="font-extrabold text-[14px] text-[var(--go)]">{fmt(totalOutstanding)}</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {customers.length === 0 && (
          <div className="text-center py-10 text-[var(--tx3)] text-[13px]">
            Customer ledger builds automatically as you log sales with customer names.
          </div>
        )}

        {customers.map((c) => (
          <button
            key={c.name}
            onClick={() => openCustomer(c.name)}
            data-testid={`customer-${c.name}`}
            className="w-full bg-white border border-[var(--br)] rounded-2xl p-3 mb-2 flex items-center gap-3 card-sh text-left"
          >
            <div className="w-11 h-11 rounded-xl bg-[var(--al)] flex items-center justify-center text-[16px] font-black text-[var(--ac)] flex-shrink-0">
              {c.name[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-bold text-[var(--tx)] truncate">{c.name}</div>
              <div className="text-[11px] text-[var(--tx3)]">
                {c.transaction_count} txn · {c.invoice_count} invoice{c.invoice_count !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="text-right">
              <div className="font-black text-[14px] text-[var(--gn)]">{fmt(c.total_spent)}</div>
              {c.outstanding > 0 ? (
                <div className="text-[10px] font-bold text-[var(--go)]">owes {fmt(c.outstanding)}</div>
              ) : (
                <div className="text-[10px] text-[var(--tx3)]">paid up</div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function CustomerDetail({ customer, onBack }) {
  const [data, setData] = useState(customer);
  const [payFor, setPayFor] = useState(null);
  const paid = data.invoices.filter((i) => i.status === "paid");
  const unpaid = data.invoices.filter((i) => i.status !== "paid");

  const onPaymentSaved = async () => {
    // Refresh customer detail
    try {
      const r = await axios.get(`${API}/customers/${encodeURIComponent(data.name)}`);
      setData(r.data);
    } catch (e) {
      console.error(e);
    }
    setPayFor(null);
  };

  return (
    <div className="flex flex-col h-full bg-[var(--bg2)]" data-testid="customer-detail">
      <div className="bg-white px-4 py-3 border-b border-[var(--br)] flex items-center gap-3">
        <button
          onClick={onBack}
          data-testid="customer-back"
          className="w-9 h-9 rounded-xl bg-[var(--sf2)] border border-[var(--br)] flex items-center justify-center text-[var(--tx2)]"
        >
          ←
        </button>
        <div className="w-11 h-11 rounded-xl bg-[var(--al)] flex items-center justify-center text-[18px] font-black text-[var(--ac)]">
          {data.name[0]?.toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="text-[var(--tx)] font-extrabold text-[16px]">{data.name}</div>
          <div className="text-[11px] text-[var(--tx3)]">
            {data.transaction_count} transactions · {data.invoice_count} invoices
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Summary card */}
        <div className="bg-[var(--ac)] rounded-3xl p-4 mb-4 text-white relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-white/10" />
          <div className="relative">
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/70 mb-1">Total Revenue from {data.name}</div>
            <div className="text-[32px] font-black leading-none mb-3">{fmt(data.total_spent)}</div>
            {data.outstanding > 0 && (
              <div className="bg-white/15 rounded-xl p-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">Outstanding Balance</div>
                <div className="text-[18px] font-black">{fmt(data.outstanding)}</div>
              </div>
            )}
          </div>
        </div>

        {unpaid.length > 0 && (
          <>
            <div className="text-[var(--tx3)] text-[11px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <KipaIcon icon={Clock} size="xs" color="var(--color-text-muted)" />
              Unpaid / Partial ({unpaid.length})
            </div>
            {unpaid.map((inv) => (
              <InvoiceRow key={inv.id} inv={inv} onRecordPayment={() => setPayFor(inv)} />
            ))}
          </>
        )}

        {paid.length > 0 && (
          <>
            <div className="text-[var(--tx3)] text-[11px] font-bold uppercase tracking-wider mb-2 mt-4 flex items-center gap-1.5">
              <KipaIcon icon={CheckCircle2} size="xs" color="var(--color-success)" />
              Paid ({paid.length})
            </div>
            {paid.map((inv) => (
              <InvoiceRow key={inv.id} inv={inv} />
            ))}
          </>
        )}

        <div className="text-[var(--tx3)] text-[11px] font-bold uppercase tracking-wider mb-2 mt-4 flex items-center gap-1.5">
          <KipaIcon icon={ScrollText} size="xs" color="var(--color-text-muted)" />
          Transactions ({data.transactions.length})
        </div>
        {data.transactions.map((t) => (
          <div
            key={t.id}
            className="bg-white border border-[var(--br)] rounded-2xl p-3 mb-2 flex items-center gap-3 card-sh"
          >
            <div className="w-10 h-10 rounded-xl bg-[var(--gl)] flex items-center justify-center flex-shrink-0">
              <KipaIcon icon={CircleDollarSign} size="sm" color="var(--color-success)" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold text-[var(--tx)] truncate">{t.description}</div>
              <div className="text-[11px] text-[var(--tx3)]">
                {formatAppDate(t.created_at)}
              </div>
            </div>
            <div className="font-black text-[14px] text-[var(--gn)]">+{fmt(t.amount)}</div>
          </div>
        ))}
      </div>

      {payFor && (
        <RecordPaymentModal
          invoice={payFor}
          onClose={() => setPayFor(null)}
          onSaved={onPaymentSaved}
        />
      )}
    </div>
  );
}

function InvoiceRow({ inv, onRecordPayment }) {
  const { fmt } = useAccountant();
  const balance = inv.total - (inv.amount_paid || 0);
  const statusColors = {
    paid: { bg: "var(--gl)", c: "var(--gn)" },
    unpaid: { bg: "var(--rl)", c: "var(--rd)" },
    partial: { bg: "var(--gdl)", c: "var(--go)" },
  };
  const s = statusColors[inv.status] || statusColors.unpaid;
  const pdfUrl = `${API}/invoices/${inv.id}/pdf`;
  const waText = encodeURIComponent(
    `Hello ${inv.customer},\n\nHere is your invoice ${inv.invoice_number} for ${inv.item} — total ${fmt(inv.total)}.` +
    (balance > 0 ? `\nBalance due: ${fmt(balance)}.` : "\nFully paid, thank you!") +
    `\n\nSent via KipaBiz.`
  );

  return (
    <div
      className="bg-white border border-[var(--br)] rounded-2xl p-3 mb-2 card-sh"
      style={{ borderLeft: `4px solid ${s.c}` }}
    >
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="text-[13px] font-extrabold text-[var(--tx)]">{inv.invoice_number}</div>
          <div className="text-[11px] text-[var(--tx3)] capitalize">{inv.item}</div>
        </div>
        <div className="text-right">
          <div className="text-[14px] font-black text-[var(--tx)]">{fmt(inv.total)}</div>
          <span
            className="inline-block text-[9px] font-bold uppercase px-2 py-0.5 rounded-full mt-0.5"
            style={{ background: s.bg, color: s.c }}
          >
            {inv.status}
          </span>
        </div>
      </div>
      {balance > 0 && (
        <div className="text-[11px] text-[var(--go)] mb-2 font-semibold">Balance: {fmt(balance)}</div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={async () => {
            try {
              await openAuthenticatedPdf(pdfUrl);
            } catch (e) {
              alert(e.message || "Could not open PDF.");
            }
          }}
          data-testid={`inv-pdf-${inv.id}`}
          className="flex-1 text-center py-1.5 rounded-lg bg-[var(--bll)] border border-[var(--bld)] text-[var(--bl)] text-[11px] font-bold inline-flex items-center justify-center gap-1"
        >
          <KipaIcon icon={Download} size="xs" color="var(--bl)" />
          PDF
        </button>
        <a
          href={`https://wa.me/?text=${waText}`}
          target="_blank"
          rel="noreferrer"
          data-testid={`inv-wa-${inv.id}`}
          className="flex-1 text-center py-1.5 rounded-lg bg-[var(--gl)] border border-[var(--gd)] text-[var(--gn)] text-[11px] font-bold inline-flex items-center justify-center gap-1"
        >
          <KipaIcon icon={Share2} size="xs" color="var(--gn)" />
          WhatsApp
        </a>
        {onRecordPayment && balance > 0 && (
          <button
            onClick={onRecordPayment}
            data-testid={`inv-pay-${inv.id}`}
            className="flex-1 text-center py-1.5 rounded-lg bg-[var(--ac)] text-white text-[11px] font-bold border-none font-sans inline-flex items-center justify-center gap-1"
          >
            <KipaIcon icon={CircleDollarSign} size="xs" color="#fff" />
            Mark paid
          </button>
        )}
      </div>
    </div>
  );
}
